import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Calculator, 
  Euro, 
  Calendar,
  ArrowUpRight,
  ChevronRight,
  Loader2,
  ArrowLeft,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickCalculator = () => {
  const navigate = useNavigate();
  
  const [indices, setIndices] = useState({ ipc: 2.9, irav: 2.32 });
  const [currentRent, setCurrentRent] = useState('');
  const [contractType, setContractType] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingIndices, setLoadingIndices] = useState(true);

  useEffect(() => {
    const fetchIndices = async () => {
      try {
        const response = await axios.get(`${API_URL}/indices`);
        setIndices(response.data);
      } catch (error) {
        console.error('Error fetching indices:', error);
      } finally {
        setLoadingIndices(false);
      }
    };
    fetchIndices();
  }, []);

  const handleCalculate = async () => {
    if (!currentRent || !contractType) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    const rent = parseFloat(currentRent);
    if (isNaN(rent) || rent <= 0) {
      toast.error('Introduce un importe de renta válido');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/calculate-quick`, {
        current_rent: rent,
        contract_type: contractType
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Error al realizar el cálculo');
    } finally {
      setLoading(false);
    }
  };

  const comparisonData = result ? [
    { name: 'IPC', value: result.new_rent_ipc, color: '#3B82F6' },
    { name: 'IRAV', value: result.new_rent_irav, color: '#10B981' }
  ] : [];

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
            
            <Button
              onClick={() => navigate('/register')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="register-button"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Crear cuenta
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="mb-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 fade-in">
          <p className="text-amber-200 text-sm text-center">
            Este es un cálculo rápido sin guardar. <strong>Crea una cuenta gratuita</strong> para guardar tu historial de cálculos.
          </p>
        </div>

        {/* Live Indices */}
        <div className="flex flex-wrap gap-4 mb-8 justify-center fade-in">
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800">
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot"></span>
            <span className="text-zinc-400 text-sm">En vivo</span>
          </div>
          <div className="index-badge index-badge-ipc">
            <span className="text-zinc-400">IPC</span>
            {loadingIndices ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="text-lg font-bold">{indices.ipc}%</span>
            )}
          </div>
          <div className="index-badge index-badge-irav">
            <span className="text-zinc-400">IRAV</span>
            {loadingIndices ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="text-lg font-bold">{indices.irav}%</span>
            )}
          </div>
        </div>

        {/* Calculator Card */}
        <Card className="glass-card border-zinc-800 fade-in stagger-1" data-testid="quick-calculator-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Calculator className="h-8 w-8 text-emerald-400" />
            </div>
            <CardTitle className="text-2xl text-white">Cálculo Rápido</CardTitle>
            <CardDescription className="text-zinc-400">
              Calcula la subida máxima de tu alquiler sin necesidad de cuenta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Rent Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">Renta actual mensual</label>
              <div className="relative">
                <Euro className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="number"
                  placeholder="850.00"
                  value={currentRent}
                  onChange={(e) => setCurrentRent(e.target.value)}
                  className="pl-12 h-14 text-xl font-mono bg-black/30 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-emerald-500/50"
                  data-testid="quick-rent-input"
                />
              </div>
            </div>

            {/* Contract Type Selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-zinc-300">Tipo de contrato</label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div
                  onClick={() => setContractType('pre_may_2023')}
                  className={`contract-card p-4 rounded-xl border cursor-pointer ${
                    contractType === 'pre_may_2023'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
                  }`}
                  data-testid="quick-contract-type-pre"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="h-5 w-5 text-blue-400" />
                    <span className="font-semibold text-white">Antes del 26/05/2023</span>
                  </div>
                  <p className="text-sm text-zinc-400">Se aplicará el IPC ({indices.ipc}%)</p>
                </div>
                
                <div
                  onClick={() => setContractType('post_may_2023')}
                  className={`contract-card p-4 rounded-xl border cursor-pointer ${
                    contractType === 'post_may_2023'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
                  }`}
                  data-testid="quick-contract-type-post"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="h-5 w-5 text-emerald-400" />
                    <span className="font-semibold text-white">Desde el 26/05/2023</span>
                  </div>
                  <p className="text-sm text-zinc-400">Se aplicará el IRAV ({indices.irav}%)</p>
                </div>
              </div>
            </div>

            {/* Calculate Button */}
            <Button
              onClick={handleCalculate}
              disabled={loading || !currentRent || !contractType}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold btn-primary-glow disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="quick-calculate-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Calculando...
                </>
              ) : (
                <>
                  Calcular Subida Máxima
                  <ChevronRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>

            {/* Result Display */}
            {result && (
              <div className="mt-6 p-6 rounded-xl bg-zinc-900/80 border border-zinc-700 fade-in" data-testid="quick-result-card">
                <div className="text-center mb-6">
                  <p className="text-zinc-400 text-sm mb-2">Nueva renta máxima aplicable</p>
                  <p className="result-value" data-testid="quick-result-new-rent">
                    {result.new_rent_applied.toFixed(2)}€
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <ArrowUpRight className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-400 font-mono">
                      +{result.increase_amount.toFixed(2)}€/mes
                    </span>
                    <span className="text-zinc-500">
                      ({result.applied_rate}%)
                    </span>
                  </div>
                </div>
                
                {/* Comparison */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-700">
                  <div className="text-center">
                    <p className="text-xs text-zinc-500 mb-1">Con IPC ({result.ipc_rate}%)</p>
                    <p className="text-lg font-mono text-blue-400">{result.new_rent_ipc.toFixed(2)}€</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-zinc-500 mb-1">Con IRAV ({result.irav_rate}%)</p>
                    <p className="text-lg font-mono text-emerald-400">{result.new_rent_irav.toFixed(2)}€</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comparison Chart */}
        {result && (
          <Card className="mt-8 glass-card border-zinc-800 fade-in" data-testid="quick-comparison-chart">
            <CardHeader>
              <CardTitle className="text-white">Comparativa IPC vs IRAV</CardTitle>
              <CardDescription className="text-zinc-400">
                Diferencia: <span className="font-mono text-emerald-400">{Math.abs(result.new_rent_ipc - result.new_rent_irav).toFixed(2)}€/mes</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData} barSize={80}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      stroke="#71717A"
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#71717A"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${value}€`}
                      domain={[result.current_rent * 0.98, 'auto']}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'rgba(24, 24, 27, 0.95)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`${value.toFixed(2)}€`, 'Nueva renta']}
                    />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {comparisonData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default QuickCalculator;
